<template>
  <div
    v-if="hovered && hoveredMenu"
    class="fixed top-0 left-0 w-screen h-screen backdrop-blur z-[999] fade-in"
    :class="dark ? 'bg-black/25' : 'bg-white/25'"
  ></div>
  <header class="fixed top-0 left-0 z-[1000] w-full transition-3" :class="headerClass">
    <div
      :class="[
        'relative w-full flex justify-center lg:border-b',
        dark ? 'lg:border-gray-70' : 'lg:border-gray-20'
      ]"
    >
      <div
        class="flex items-center justify-between"
        :class="[
          size === 'lg' ? 'w-[1200px] h-40' : 'w-full h-28 pr-8',
          sideBarOpened ? 'bg-white' : ''
        ]"
      >
        <div class="flex items-center gap-32 px-8 lg:px-0">
          <RouterLink v-if="!sideBarOpened" to="/">
            <img src="/logo/logo.svg" alt="CoMake" class="w-[78px] lg:w-[97px]" />
          </RouterLink>
          <SelectLanaguage v-else />

          <Tab
            v-if="mainLinks && size === 'lg'"
            class="flex h-full"
            :labels="mainLinks.map((link) => link.text)"
            :activeIndex="mainLinks.findIndex((link) => link.key === menu)"
            :hovered-index="mainLinks.findIndex((link) => link.key === hoveredMenu)"
            :dark="dark"
            @hover="updateHovered"
            @update:modelValue="moveToIndex"
            @mouseenter="openMenu"
            @mouseleave="handleTabLeave"
          />
        </div>

        <div v-if="size === 'lg'" class="flex items-center gap-12">
          <Btn
            type="primary"
            size="md"
            :text="$t('menu.contact_us')"
            :icon="{ icon: 'lucide:arrow-right' }"
            icon-direction="right"
            :dark="dark"
            @click="moveTo('/contact')"
          />
          <SelectLanaguage :dark="dark" />
        </div>
        <div v-else class="flex items-center gap-10">
          <RouterLink v-if="sideBarOpened" to="/" @click="closeSideBar">
            <Icon icon="akar-icons:home" color="#161817" width="24" />
          </RouterLink>
          <RouterLink to="/contact" @click="closeSideBar">
            <Icon
              icon="tabler:mail"
              :color="dark && !sideBarOpened ? 'white' : '#161817'"
              width="24"
            />
          </RouterLink>
          <button v-if="sideBarOpened" @click="toggleIsOpen">
            <Icon icon="ci:close-md" width="24" color="#161817" />
          </button>
          <button v-if="!sideBarOpened" @click="toggleIsOpen" class="flex items-center">
            <Icon
              icon="material-symbols:menu-rounded"
              width="24"
              :color="dark ? 'white' : '#161817'"
            />
          </button>
        </div>
      </div>

      <MenuDetail
        v-if="hovered && hoveredMenu"
        :menu="hoveredMenu"
        :currentLink="link"
        class="absolute top-[81px] left-0"
        :dark="dark"
        @mouseenter="handleDetailEnter"
        @mouseleave="hovered = false"
        @move-router="hovered = false"
      />
    </div>
  </header>
  <SideBar v-if="size === 'md'" :isOpen="sideBarOpened" @close="toggleIsOpen" />
</template>

<script setup lang="ts">
import { Icon } from '@iconify/vue'
import Btn from '@/shared/ui/components/button/Btn.vue'
import Tab from '@/shared/ui/components/navigation/Tab.vue'
import MenuDetail from '@/shared/ui/components/navigation/MenuDetail.vue'
import SideBar from '@/shared/ui/components/navigation/SideBar.vue'
import SelectLanaguage from '@/features/change-language/ui/SelectLanguage.vue'
import { ref, onMounted, watch, onUnmounted, computed } from 'vue'
import { useRoute } from 'vue-router'
import { getMainLinks } from '@/app/router/navigation'
import { useRouter } from 'vue-router'

type Props = {
  size: 'lg' | 'md'
  dark?: boolean
}
const props = defineProps<Props>()

const menu = ref<string>('robots')
const link = ref<string>('')
const sideBarOpened = ref<boolean>(false)
const route = useRoute()
const router = useRouter()
const hovered = ref(false)
const hoveredMenu = ref<string>('')
const scrollY = ref(0)

const mainLinks = ref<RouteLink[]>()

onMounted(() => {
  menu.value = getMenuFromRoute()
  link.value = window.location.pathname
  mainLinks.value = getMainLinks()

  window.addEventListener('scroll', handleScroll)
})

onUnmounted(() => {
  document.body.style.overflow = ''
  // 스크롤 이벤트 리스너 제거
  window.removeEventListener('scroll', handleScroll)
})

watch(
  () => route,
  () => {
    menu.value = getMenuFromRoute()
    link.value = window.location.pathname
  },
  { deep: true }
)

watch(hovered, (newVal) => {
  if (!newVal) {
    hoveredMenu.value = ''
  }
})

watch(sideBarOpened, (newVal) => {
  if (newVal) {
    document.body.style.overflow = 'hidden'
  } else {
    document.body.style.overflow = ''
  }
})

const handleScroll = () => {
  scrollY.value = window.scrollY
}

const headerClass = computed(() => {
  const scollBackground =
    scrollY.value > 10
      ? menu.value === ''
        ? 'backdrop-blur-sm bg-black/75'
        : 'backdrop-blur-sm bg-white/75'
      : 'bg-white/0'

  const munuBackground =
    hovered.value && hoveredMenu.value
      ? menu.value === ''
        ? 'bg-gray-80'
        : 'bg-gray-5'
      : scollBackground

  return [munuBackground].join(' ')
})

function getMenuFromRoute() {
  const path = window.location.pathname
  const category = path.split('/')[1]
  return category
}

const toggleIsOpen = () => {
  sideBarOpened.value = !sideBarOpened.value
}

const closeSideBar = () => {
  sideBarOpened.value = false
}

const moveToIndex = (index: number) => {
  if (!mainLinks.value) return
  const targetLink = mainLinks.value[index]
  if (targetLink) {
    router.push(targetLink.to)
    hovered.value = false
  }
}

const moveTo = (link: string) => {
  closeSideBar()
  router.push(link)
}

let leaveTimeout: ReturnType<typeof setTimeout> | null = null

const openMenu = () => {
  if (leaveTimeout) {
    clearTimeout(leaveTimeout)
    leaveTimeout = null
  }
  hovered.value = true
}

const updateHovered = (index: number) => {
  if (!mainLinks.value) return
  hoveredMenu.value = mainLinks.value[index].key
  openMenu()
}

const handleTabLeave = () => {
  leaveTimeout = setTimeout(() => {
    hovered.value = false
  }, 100)
}

const handleDetailEnter = () => {
  openMenu()
}
</script>
